import React, { useMemo, useRef, useState } from "react";
import { fmtQty } from "./executionNorm";
import { chNumber, classColor, rangesOverlap } from "./driveChainShared";

// What this face has ALREADY taken: one strip, one scale.
//
// Earlier attempts failed in opposite directions — a badge per reach is
// unreadable on a 2 km heading, and a strip plus a zoomed second lane made the
// reader work out how two pictures related. So: a single proportional bar, and
// the numbers come from HOVER rather than from labels that collide.
//
// The stretches being typed are drawn on the same scale directly beneath the
// ground they claim: green when clear, red when they reach back into captured
// ground. Those two colours mean only that; the bar above keeps the geology
// palette shared with the Drives page.

const W = 920; // viewBox units; the svg itself is width:100%
const PAD = 24;
// Vertical rhythm, in viewBox units. BAR_Y leaves room for the face label
// ABOVE the bar — at y=12 its 12px glyphs were clipped by the viewBox top.
const BAR_Y = 22;
const BAR_H = 26;
const MINE_Y = 52;
const MINE_H = 10;
const AXIS_Y = (hasTyped: boolean) => (hasTyped ? 78 : 64);
const CLEAR = "#2b9a66";
const CLASH = "#cf1322";
const FACE = "#2557d6";

export interface ChainReach {
  fromChainage: string;
  toChainage: string;
  length: string;
  slotKey: string;
  classLabel: string;
  firstDate: string;
  lastDate: string;
  segmentCount: number;
}

export interface ChainGap {
  fromChainage: string;
  toChainage: string;
  length: string;
}

export interface TypedStretch {
  from: number;
  to: number;
  label: string;
}

interface Props {
  chain: {
    faceChainage?: string;
    coveredLength?: string;
    percentByChainage?: string;
    lastCaptureDate?: string;
    startChainage?: string;
    endChainage?: string;
    reaches?: ChainReach[];
    gaps?: ChainGap[];
  } | null;
  /** Complete stretches on the form right now. */
  stretches?: TypedStretch[];
  /**
   * Off while editing an existing entry: that entry's own ground is already in
   * the chain, so every row would accuse itself.
   */
  markClashes?: boolean;
}

const asNum = (v: unknown) => (v === "" || v == null ? null : Number(v));

/** What the designed tooltip shows for whichever band is under the cursor. */
interface HoverCard {
  kind: "reach" | "gap" | "mine";
  swatch: string;
  title: string;
  range: string;
  length: string;
  meta?: string;
  warn?: string;
  x: number; // px within the chart wrapper
}

const CapturedChain: React.FC<Props> = ({ chain, stretches = [], markClashes = true }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<HoverCard | null>(null);
  // Anchored to the BAND, not the cursor: one state update per band entered
  // rather than one per mouse move — a long drive is hundreds of bands, and
  // re-rendering all of them on every pixel of travel is how a chart stutters.
  // It also keeps the card still while it is being read.
  const track = (card: Omit<HoverCard, "x">, centreInViewBox: number) => ({
    onMouseEnter: () => {
      const width = wrapRef.current?.clientWidth ?? 0;
      setHover({ ...card, x: (centreInViewBox / W) * width });
    },
    onMouseLeave: () => setHover(null),
  });
  const model = useMemo(() => {
    const reaches = (chain?.reaches || []).map((r) => ({
      from: Math.min(Number(r.fromChainage), Number(r.toChainage)),
      to: Math.max(Number(r.fromChainage), Number(r.toChainage)),
      label: r.classLabel || r.slotKey,
      length: r.length,
      dates:
        r.firstDate && r.lastDate && r.firstDate !== r.lastDate
          ? `${r.firstDate} → ${r.lastDate}`
          : r.lastDate || r.firstDate,
    }));
    if (!reaches.length) return null;
    const gaps = (chain?.gaps || []).map((g) => ({
      from: Math.min(Number(g.fromChainage), Number(g.toChainage)),
      to: Math.max(Number(g.fromChainage), Number(g.toChainage)),
      length: g.length,
    }));
    const typed = stretches
      .filter((s) => Number.isFinite(s.from) && Number.isFinite(s.to) && s.from !== s.to)
      .map((s) => {
        const from = Math.min(s.from, s.to);
        const to = Math.max(s.from, s.to);
        return {
          from,
          to,
          label: s.label,
          hits: markClashes ? reaches.filter((r) => rangesOverlap(from, to, r.from, r.to)) : [],
        };
      });

    const face = asNum(chain?.faceChainage);
    const design = [asNum(chain?.startChainage), asNum(chain?.endChainage)].filter(
      (v): v is number => v != null
    );
    const points = [
      ...design,
      ...reaches.flatMap((r) => [r.from, r.to]),
      ...typed.flatMap((t) => [t.from, t.to]),
      ...(face == null ? [] : [face]),
    ];
    const min = Math.min(...points);
    const max = Math.max(...points);
    return { reaches, gaps, typed, face, min, max };
  }, [chain, stretches, markClashes]);

  if (!model) return null;
  const { reaches, gaps, typed, face, min, max } = model;

  const x = (ch: number) => PAD + ((ch - min) / (max - min || 1)) * (W - PAD * 2);
  // A 3 m reach on a 2 km drive is still worth a sliver rather than nothing.
  const band = (from: number, to: number, floor = 2) => {
    const a = x(from);
    const b = x(to);
    return { x: Math.min(a, b), width: Math.max(floor, Math.abs(b - a)) };
  };
  const clashing = typed.filter((t) => t.hits.length);
  const faceX = face == null ? null : x(face);
  // The face label carries its own number, so the right-hand end label would
  // only collide with it.
  const showMaxLabel = faceX == null || faceX < W - PAD - 60;

  return (
    <div className="pms-exec-chain">
      <div className="pms-exec-chain-head">
        <span className="pms-eyebrow">Already captured on this face</span>
        {face != null && (
          <span className="pms-exec-chain-face">
            face at <b>CH {chNumber(face)}</b>
            <span className="pms-exec-hint">
              {chain?.percentByChainage
                ? ` · ${chain.percentByChainage}% by chainage`
                : ` · ${fmtQty(chain?.coveredLength || "")} m covered`}
              {chain?.lastCaptureDate ? ` · last captured ${chain.lastCaptureDate}` : ""}
            </span>
          </span>
        )}
      </div>

      <div className="pms-exec-chain-plot" ref={wrapRef}>
      {hover && (() => {
        // The card is clamped to stay inside the chart; the arrow then has to
        // travel the other way so it still points at the band being described.
        const width = wrapRef.current?.clientWidth ?? 0;
        const left = Math.min(Math.max(hover.x, 112), Math.max(112, width - 112));
        const arrow = Math.max(-70, Math.min(70, hover.x - left));
        return (
        <div
          className={`pms-exec-chain-tip${hover.warn ? " pms-exec-chain-tip--warn" : ""}`}
          style={{ left, ["--tip-arrow" as any]: `${arrow}px` }}
        >
          <div className="pms-exec-chain-tip-head">
            {hover.swatch && <i style={{ background: hover.swatch }} />}
            {hover.title}
          </div>
          <div className="pms-exec-chain-tip-range">
            {hover.range}
            <span>{hover.length}</span>
          </div>
          {hover.meta && <div className="pms-exec-chain-tip-meta">{hover.meta}</div>}
          {hover.warn && <div className="pms-exec-chain-tip-warn">{hover.warn}</div>}
        </div>
        );
      })()}
      <svg
        viewBox={`0 0 ${W} ${typed.length ? 84 : 70}`}
        style={{ width: "100%", display: "block" }}
        role="img"
        aria-label={`The drive from chainage ${min} to ${max}: captured reaches by rock class, free ground, and the face position`}
      >
        {/* the drive */}
        <rect x={PAD} y={BAR_Y} width={W - PAD * 2} height={BAR_H} rx={4} fill="#f7f9fc" stroke="#dfe4ee" />

        {gaps.map((gap, i) => {
          const b = band(gap.from, gap.to);
          return (
            <rect
              key={`g${i}`}
              x={b.x}
              y={BAR_Y}
              width={b.width}
              height={BAR_H}
              fill="#fff"
              stroke="#dfe4ee"
              strokeDasharray="3 3"
              aria-label={`Free ground, chainage ${chNumber(gap.from)} to ${chNumber(gap.to)}`}
              {...track({
                kind: "gap",
                swatch: "",
                title: "Free ground",
                range: `CH ${chNumber(gap.from)} → ${chNumber(gap.to)}`,
                length: `${fmtQty(gap.length)} m`,
                meta: "Nothing captured here yet",
              }, b.x + b.width / 2)}
            />
          );
        })}

        {reaches.map((reach, i) => {
          const b = band(reach.from, reach.to);
          const taken = typed.some((t) => t.hits.includes(reach));
          return (
            <g key={`r${i}`}>
              <rect x={b.x} y={BAR_Y} width={b.width} height={BAR_H} fill={classColor(reach.label)} />
              {taken && (
                <rect x={b.x} y={BAR_Y} width={b.width} height={BAR_H} fill="none" stroke={CLASH} strokeWidth={2.5} />
              )}
              {/* One hit target for the whole band — hover gives the numbers so
                  the picture does not need labels that collide. */}
              <rect
                x={b.x}
                y={BAR_Y}
                width={b.width}
                height={BAR_H}
                fill="transparent"
                aria-label={`${reach.label}, chainage ${chNumber(reach.from)} to ${chNumber(reach.to)}, ${fmtQty(
                  reach.length
                )} metres`}
                {...track({
                  kind: "reach",
                  swatch: classColor(reach.label),
                  title: reach.label,
                  range: `CH ${chNumber(reach.from)} → ${chNumber(reach.to)}`,
                  length: `${fmtQty(reach.length)} m`,
                  meta: reach.dates ? `captured ${reach.dates}` : undefined,
                  warn: taken ? "The stretch you typed reaches into this" : undefined,
                }, b.x + b.width / 2)}
              />
            </g>
          );
        })}

        {/* what is being typed, on the same scale, under the ground it claims */}
        {typed.map((stretch, i) => {
          const b = band(stretch.from, stretch.to, 3);
          const bad = stretch.hits.length > 0;
          return (
            <rect
              key={`s${i}`}
              x={b.x}
              y={MINE_Y}
              width={b.width}
              height={MINE_H}
              rx={2}
              fill={bad ? CLASH : CLEAR}
              aria-label={`Your stretch ${chNumber(stretch.from)} to ${chNumber(stretch.to)}, ${
                bad ? "overlaps captured ground" : "clear"
              }`}
              {...track({
                kind: "mine",
                swatch: bad ? CLASH : CLEAR,
                title: `Your stretch · ${stretch.label}`,
                range: `CH ${chNumber(stretch.from)} → ${chNumber(stretch.to)}`,
                length: `${fmtQty(stretch.to - stretch.from)} m`,
                meta: bad ? undefined : "Clear of captured ground",
                warn: bad ? `Overlaps ${stretch.hits.map((h) => h.label).join(", ")}` : undefined,
              }, b.x + b.width / 2)}
            />
          );
        })}

        {faceX != null && (
          <>
            <line x1={faceX} y1={BAR_Y - 6} x2={faceX} y2={(typed.length ? MINE_Y + MINE_H : BAR_Y + BAR_H) + 4} stroke={FACE} strokeWidth={2.5} />
            <text x={Math.min(faceX, W - PAD)} y={BAR_Y - 8} textAnchor="end" fontSize={12} fill={FACE} fontWeight={600}>
              face {chNumber(face)}
            </text>
          </>
        )}

        <text x={PAD} y={AXIS_Y(!!typed.length)} fontSize={12} fill="#8094ae">{chNumber(min)}</text>
        {showMaxLabel && (
          <text x={W - PAD} y={AXIS_Y(!!typed.length)} textAnchor="end" fontSize={12} fill="#8094ae">
            {chNumber(max)}
          </text>
        )}
      </svg>
      </div>

      <div className="pms-exec-chain-legend">
        <span>Hover any band for its class, chainage and dates</span>
        <span><i className="pms-exec-chain-key pms-exec-chain-key--free" /> free</span>
        <span><i className="pms-exec-chain-key" style={{ background: CLEAR }} /> your stretch</span>
        <span><i className="pms-exec-chain-key" style={{ background: CLASH }} /> overlaps captured ground</span>
      </div>

      {clashing.length > 0 ? (
        <div className="pms-exec-hint pms-exec-hint--red" style={{ marginTop: 4 }}>
          {clashing
            .map((s) => `CH ${chNumber(s.from)} → ${chNumber(s.to)} reaches into ${s.hits.map((h) => h.label).join(", ")}`)
            .join(" · ")}
        </div>
      ) : (
        <div className="pms-exec-hint" style={{ marginTop: 4 }}>
          {face != null ? (
            <>
              Carry on from <b>CH {chNumber(face)}</b>. A stretch that reaches back into captured ground is
              rejected — one metre of tunnel is one rock class.
            </>
          ) : (
            <>A stretch that reaches back into captured ground is rejected — one metre of tunnel is one rock class.</>
          )}
        </div>
      )}
    </div>
  );
};

export default CapturedChain;
